import { useMemo } from "react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Lock, Plus } from "lucide-react";
import type { SectionType } from "@profolio/validators";
import { SECTION_METADATA, ALL_SECTION_TYPES } from "./types";

export interface AddSectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  availableTypes?: SectionType[];
  existingTypes?: SectionType[];
  isSectionLimitReached?: boolean;
  onAddSection?: (type: SectionType) => void;
  onSelectType?: (type: SectionType) => void;
  onUpgradeClick?: () => void;
}

export function AddSectionModal({
  open,
  onOpenChange,
  availableTypes: propAvailableTypes,
  existingTypes,
  isSectionLimitReached = false,
  onAddSection,
  onSelectType,
  onUpgradeClick,
}: AddSectionModalProps) {
  const handleSelect = onAddSection || onSelectType || (() => {});

  const available = useMemo(() => {
    if (propAvailableTypes) return propAvailableTypes;
    if (existingTypes) {
      const existingSet = new Set(existingTypes);
      return ALL_SECTION_TYPES.filter((t) => !existingSet.has(t) || t === "media");
    }
    return ALL_SECTION_TYPES;
  }, [propAvailableTypes, existingTypes]);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Add Portfolio Section"
      description="Choose an academic section to showcase your work, publications, and experience."
    >
      <div className="space-y-4 pt-2">
        {isSectionLimitReached && (
          <div className="p-3.5 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-xl flex items-center justify-between gap-3 text-xs">
            <div className="flex items-center gap-2 text-amber-900 dark:text-amber-200">
              <Lock className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
              <span>
                <strong>5-Section Limit:</strong> Starter tier allows up to 5 sections. Upgrade to Pro for unlimited sections.
              </span>
            </div>
            {onUpgradeClick && (
              <Button
                size="sm"
                onClick={() => {
                  onOpenChange(false);
                  onUpgradeClick();
                }}
                className="h-8 text-xs bg-amber-600 hover:bg-amber-700 text-white font-medium shrink-0 shadow-2xs cursor-pointer"
              >
                Upgrade
              </Button>
            )}
          </div>
        )}

        {available.length === 0 ? (
          <div className="text-center py-10 text-sm text-muted-foreground">
            All available academic sections have already been added to your profile!
          </div>
        ) : (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-1"
            role="list"
            aria-label="Available portfolio sections"
          >
            {available.map((type) => {
              const meta = SECTION_METADATA[type];
              const Icon = meta?.icon;
              return (
                <button
                  key={type}
                  type="button"
                  disabled={isSectionLimitReached}
                  aria-label={`Add ${meta?.label || type} section`}
                  onClick={() => {
                    if (!isSectionLimitReached) {
                      handleSelect(type);
                    }
                  }}
                  className={`flex flex-col justify-between p-3.5 rounded-xl border text-left transition-all outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                    isSectionLimitReached
                      ? "opacity-50 bg-muted/30 border-border cursor-not-allowed"
                      : "bg-card hover:bg-muted/40 hover:border-primary/50 cursor-pointer shadow-xs group"
                  }`}
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors shadow-2xs">
                      {Icon && <Icon className="h-5 w-5" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="text-sm font-semibold text-foreground truncate">
                        {meta?.label || type}
                      </h4>
                      <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                        {meta?.description}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-end pt-2 border-t border-border/60">
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary group-hover:underline">
                      <Plus className="h-3.5 w-3.5" />
                      Add Section
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </Dialog>
  );
}
